import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { consumeRecoveryCode } from "@/lib/recovery-codes.functions";
import { z } from "zod";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/hooks/use-auth";
import { useEffect } from "react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Shield, Lock, FileCheck } from "lucide-react";

/**
 * Only allow same-origin internal paths as redirect targets.
 * Rejects protocol-relative URLs, absolute URLs, and anything with control chars/whitespace.
 */
export function sanitizeRedirect(raw: string | undefined | null): string {
  const fallbackPath = "/account";
  if (!raw || typeof raw !== "string") return fallbackPath;
  const trimmed = raw.trim();
  if (!trimmed.startsWith("/")) return fallbackPath;
  if (trimmed.startsWith("//") || trimmed.startsWith("/\\")) return fallbackPath;
  if (/[\s\x00-\x1f]/.test(trimmed)) return fallbackPath;
  return trimmed;
}

const authSearchSchema = z.object({
  redirect: fallback(z.string().transform(sanitizeRedirect), "/account").default("/account"),
});

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Inloggen of registreren — Elvion Group Nederland" },
      { name: "description", content: "Log in op je klantaccount of registreer als particulier of zakelijke klant." },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "https://elvion-group.nl/auth" }],
  }),
  validateSearch: zodValidator(authSearchSchema),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const { session, loading } = useAuth();
  const { redirect: redirectTo } = Route.useSearch();

  useEffect(() => {
    if (!loading && session) {
      // Use full navigate so search-driven deep links land on the intended page.
      window.location.replace(redirectTo);
    }
  }, [session, loading, redirectTo, navigate]);


  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="border-b border-border bg-gradient-to-b from-muted/40 to-background">
          <div className="container mx-auto px-4 py-12 md:py-16">
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground hover:underline">
              ← Terug naar website
            </Link>
            <div className="mt-4 max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-muted-foreground">
                <Lock className="h-3 w-3" /> Beveiligde omgeving
              </span>
              <h1 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                Klantportaal
              </h1>
              <p className="mt-3 text-base md:text-lg text-muted-foreground">
                Log in om je prijzen te bekijken, offertes te ondertekenen, bestellingen te plaatsen en je documenten te beheren.
              </p>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="container mx-auto px-4 py-12 md:py-16">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.1fr] lg:gap-16 max-w-6xl mx-auto">
            {/* Info panel */}
            <aside className="space-y-6">
              <h2 className="text-2xl font-semibold text-foreground">Wat kun je in het portaal?</h2>
              <ul className="space-y-4">
                <li className="flex gap-3">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <FileCheck className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="font-medium text-foreground">Offertes & bestellingen</p>
                    <p className="text-sm text-muted-foreground">Bekijk, accordeer en volg je opdrachten online.</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Shield className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="font-medium text-foreground">Zakelijke prijzen</p>
                    <p className="text-sm text-muted-foreground">Na verificatie van je KvK krijg je toegang tot inkoopprijzen.</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Lock className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="font-medium text-foreground">Veilig & AVG-proof</p>
                    <p className="text-sm text-muted-foreground">Versleutelde verbinding en strikte toegangscontrole.</p>
                  </div>
                </li>
              </ul>
              <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
                Nog geen zakelijke klant? Maak rechts een account aan en upload je KvK-uittreksel. Wij activeren je account doorgaans binnen één werkdag.
              </div>
            </aside>

            {/* Auth card */}
            <div className="rounded-xl border border-border bg-card p-6 md:p-8 shadow-sm">
              <Tabs defaultValue="consumer" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="consumer">Particulier</TabsTrigger>
                  <TabsTrigger value="business">Zakelijk</TabsTrigger>
                </TabsList>
                <TabsContent value="consumer" className="mt-6">
                  <ConsumerAuth />
                </TabsContent>
                <TabsContent value="business" className="mt-6">
                  <BusinessAuth />
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function ConsumerAuth() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const magicLink = async () => {
    const parsed = z.string().email().max(255).safeParse(email.trim());
    if (!parsed.success) return toast.error("Vul een geldig e-mailadres in");
    setBusy(true);
    const { error } = await supabase.auth.signInWithOtp({
      email: parsed.data,
      options: {
        emailRedirectTo: `${window.location.origin}/account`,
        data: { customer_type: "consumer" },
      },
    });
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Check je e-mail voor de inloglink.");
  };

  const google = async () => {
    setBusy(true);
    const result = await lovable.auth.signInWithOAuth("google", { redirect_uri: window.location.origin });
    if (result.error) {
      setBusy(false);
      toast.error("Google login mislukt");
    }
  };

  return (
    <div className="space-y-4">
      <Button onClick={google} disabled={busy} variant="outline" className="w-full">
        Doorgaan met Google
      </Button>
      <div className="relative my-4 text-center text-xs uppercase">
        <span className="bg-background px-2 text-muted-foreground">of</span>
        <div className="absolute inset-0 top-1/2 -z-10 border-t border-border" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="c-email">E-mail (magic link)</Label>
        <Input id="c-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jij@email.nl" />
      </div>
      <Button onClick={magicLink} disabled={busy} className="w-full">Stuur inloglink</Button>
    </div>
  );
}

const businessSchema = z.object({
  email: z.string().trim().email("Ongeldig e-mailadres").max(255),
  password: z.string().min(8, "Minimaal 8 tekens").max(128),
  full_name: z.string().trim().min(2).max(120),
  phone: z.string().trim().min(6).max(30),
  company_name: z.string().trim().min(2).max(120),
  kvk_number: z.string().trim().regex(/^\d{8}$/, "8 cijfers"),
  vat_number: z.string().trim().max(20).optional().or(z.literal("")),
});

function BusinessAuth() {
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  return (
    <div className="space-y-4">
      <div className="flex gap-2 text-sm">
        <button
          className={mode === "signin" ? "font-semibold text-primary" : "text-muted-foreground"}
          onClick={() => setMode("signin")}
        >Inloggen</button>
        <span className="text-muted-foreground">·</span>
        <button
          className={mode === "signup" ? "font-semibold text-primary" : "text-muted-foreground"}
          onClick={() => setMode("signup")}
        >Account aanmaken</button>
      </div>
      {mode === "signin" ? <BusinessSignIn /> : <BusinessSignUp />}
    </div>
  );
}

function BusinessSignIn() {
  const consumeFn = useServerFn(consumeRecoveryCode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState<{ factorId: string } | null>(null);
  const [code, setCode] = useState("");
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");

  const submit = async () => {
    setBusy(true);
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    if (error) {
      setBusy(false);
      return toast.error(error.message);
    }
    const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (aal?.nextLevel === "aal2" && aal.currentLevel !== "aal2") {
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const factor = factors?.totp?.find((f) => f.status === "verified");
      if (factor) {
        setBusy(false);
        setMfa({ factorId: factor.id });
        return;
      }
    }
    setBusy(false);
    toast.success("Welkom terug");
  };

  const verifyMfa = async () => {
    if (!mfa) return;
    setBusy(true);
    const { error } = await supabase.auth.mfa.challengeAndVerify({
      factorId: mfa.factorId,
      code: code.trim(),
    });
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Welkom terug");
    setMfa(null);
    setCode("");
  };

  const useRecovery = async () => {
    setBusy(true);
    try {
      await consumeFn({ data: { code: recoveryCode.trim() } });
      toast.success("Toegang hersteld. Stel direct een nieuwe authenticator-app in.");
      setMfa(null);
      setRecoveryMode(false);
      setRecoveryCode("");
      // Force a fresh session/AAL state
      window.location.href = "/account/beveiliging";
    } catch (e: any) {
      toast.error(e?.message ?? "Herstelcode ongeldig.");
    }
    setBusy(false);
  };

  const cancelMfa = async () => {
    await supabase.auth.signOut();
    setMfa(null);
    setCode("");
    setRecoveryMode(false);
    setRecoveryCode("");
  };

  const reset = async () => {
    if (!email) return toast.error("Vul je e-mail in");
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) return toast.error(error.message);
    toast.success("Reset-link verstuurd");
  };

  if (mfa) {
    return (
      <div className="space-y-4">
        {recoveryMode ? (
          <>
            <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm">
              Voer een van uw eenmalige herstelcodes in (formaat <code>XXXXX-XXXXX</code>). 2FA wordt
              tijdelijk uitgeschakeld zodat u opnieuw kunt instellen.
            </div>
            <div className="space-y-2">
              <Label>Herstelcode</Label>
              <Input
                autoComplete="one-time-code"
                value={recoveryCode}
                onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                placeholder="ABCDE-12345"
              />
            </div>
            <Button
              onClick={useRecovery}
              disabled={busy || recoveryCode.length < 10}
              className="w-full"
            >
              Toegang herstellen
            </Button>
            <button
              type="button"
              onClick={() => setRecoveryMode(false)}
              className="text-xs text-muted-foreground hover:underline"
            >
              Terug naar authenticator-code
            </button>
          </>
        ) : (
          <>
            <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm">
              Voer de 6-cijferige code uit uw authenticator-app in.
            </div>
            <div className="space-y-2">
              <Label>Verificatiecode</Label>
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
              />
            </div>
            <Button onClick={verifyMfa} disabled={busy || code.length !== 6} className="w-full">
              Bevestigen
            </Button>
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setRecoveryMode(true)}
                className="text-xs text-muted-foreground hover:underline"
              >
                Authenticator kwijt? Gebruik een herstelcode
              </button>
              <button
                type="button"
                onClick={cancelMfa}
                className="text-xs text-muted-foreground hover:underline"
              >
                Annuleren
              </button>
            </div>
          </>
        )}
      </div>
    );
  }



  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>E-mail</Label>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className="space-y-2">
        <Label>Wachtwoord</Label>
        <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
      </div>
      <Button onClick={submit} disabled={busy} className="w-full">Inloggen</Button>
      <div className="text-center">
        <Link to="/inloggegevens-vergeten" className="text-xs text-muted-foreground hover:underline">
          Wachtwoord vergeten?
        </Link>
      </div>
    </div>
  );
}



function BusinessSignUp() {
  const [form, setForm] = useState({
    email: "", password: "", full_name: "", phone: "",
    company_name: "", kvk_number: "", vat_number: "",
  });
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    const parsed = businessSchema.safeParse(form);
    if (!parsed.success) return toast.error(parsed.error.issues[0]?.message ?? "Controleer je gegevens");
    if (!file) return toast.error("KvK-uittreksel is verplicht");
    if (file.size > 10 * 1024 * 1024) return toast.error("Bestand max 10MB");
    const okType = ["application/pdf", "image/jpeg", "image/png"].includes(file.type);
    if (!okType) return toast.error("Alleen PDF, JPG of PNG");

    setBusy(true);
    const { data: signUp, error: suErr } = await supabase.auth.signUp({
      email: parsed.data.email,
      password: parsed.data.password,
      options: {
        emailRedirectTo: `${window.location.origin}/account`,
        data: {
          customer_type: "business",
          full_name: parsed.data.full_name,
          phone: parsed.data.phone,
        },
      },
    });
    if (suErr || !signUp.user) { setBusy(false); return toast.error(suErr?.message ?? "Aanmaken mislukt"); }

    // Upload KvK doc
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "bin";
    const path = `${signUp.user.id}/uittreksel-${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage.from("kvk-documents").upload(path, file, { upsert: true });
    if (upErr) { setBusy(false); return toast.error("Upload mislukt: " + upErr.message); }

    const { error: bdErr } = await supabase.from("business_details").insert({
      user_id: signUp.user.id,
      company_name: parsed.data.company_name,
      kvk_number: parsed.data.kvk_number,
      vat_number: parsed.data.vat_number || null,
      kvk_document_path: path,
    });
    setBusy(false);
    if (bdErr) return toast.error(bdErr.message);
    toast.success("Account aangemaakt — in afwachting van goedkeuring.");
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label>Contactpersoon</Label><Input value={form.full_name} onChange={set("full_name")} /></div>
        <div className="space-y-1"><Label>Telefoon</Label><Input value={form.phone} onChange={set("phone")} /></div>
      </div>
      <div className="space-y-1"><Label>Bedrijfsnaam</Label><Input value={form.company_name} onChange={set("company_name")} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label>KvK-nummer</Label><Input value={form.kvk_number} onChange={set("kvk_number")} placeholder="12345678" /></div>
        <div className="space-y-1"><Label>BTW-nummer</Label><Input value={form.vat_number} onChange={set("vat_number")} /></div>
      </div>
      <div className="space-y-1"><Label>E-mail</Label><Input type="email" value={form.email} onChange={set("email")} /></div>
      <div className="space-y-1"><Label>Wachtwoord (min 8 tekens)</Label><Input type="password" value={form.password} onChange={set("password")} /></div>
      <div className="space-y-1">
        <Label>KvK-uittreksel (PDF/JPG/PNG, max 10MB) *</Label>
        <Input type="file" accept=".pdf,image/jpeg,image/png" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      </div>
      <p className="text-xs text-muted-foreground">Je account wordt handmatig gecontroleerd voor je prijzen en bestellingen kunt zien.</p>
      <Button onClick={submit} disabled={busy} className="w-full">Account aanmaken</Button>
    </div>
  );
}
